import { useMemo } from 'react';
import { useQuery } from 'urql';

import {
  type CompositionV2Filters,
  PAY_V2_QUERY,
  type PayV2Req,
  PositionsEntityKind,
} from '@revelio/data-access';

import type { SalaryToggles } from '../IndividualPotentialPage';
import { applyGeoFilter } from '../utils/applyGeoFilter';
import {
  buildCustomRoleFilter,
  type GeoLevel,
  type ResolvedProfileIds,
  type RoleLevel,
} from './useResolveProfileIds';

export type CompanyEntityType =
  | 'company'
  | 'rics_k10'
  | 'rics_k50'
  | 'rics_k400';

export interface CompanyEntity {
  type: CompanyEntityType;
  id: number;
}

const COMPANY_TYPE_TO_ENTITY: Record<CompanyEntityType, PositionsEntityKind> = {
  company: PositionsEntityKind.Company,
  rics_k10: PositionsEntityKind.RicsK10,
  rics_k50: PositionsEntityKind.RicsK50,
  rics_k400: PositionsEntityKind.RicsK400,
};

const COMPANY_TYPE_TO_FILTER_KEY: Record<
  CompanyEntityType,
  keyof CompositionV2Filters
> = {
  company: 'company',
  rics_k10: 'rics_k10',
  rics_k50: 'rics_k50',
  rics_k400: 'rics_k400',
};

export interface DistributionPoint {
  value: number;
  kde: number;
  cdf: number;
}

export interface SalaryDistributionResult {
  distribution: DistributionPoint[];
  label: string;
  percentile: number | null;
  median: number | null;
  p10: number | null;
  p90: number | null;
  isLoading: boolean;
  error: string | null;
}

export const BUCKET_SIZE = 1000; // base_salary bucket size

export const findPercentileValue = (
  distribution: DistributionPoint[],
  targetCdf: number
): number | null => {
  for (let i = 0; i < distribution.length; i++) {
    const point = distribution[i];
    if (point && point.cdf >= targetCdf) {
      return point.value;
    }
  }
  return null;
};

export const findUserPercentile = (
  distribution: DistributionPoint[],
  salary: number
): number | null => {
  if (distribution.length === 0 || salary <= 0) return null;

  for (let i = 0; i < distribution.length; i++) {
    const point = distribution[i];
    if (point && point.value >= salary) {
      return Math.round(point.cdf * 100);
    }
  }
  return 100;
};

export const buildDistributionFromHistogram = (
  histogramData: number[],
  bucketSize: number
): DistributionPoint[] => {
  const totalCount = histogramData.reduce(
    (sum, count) => sum + (count || 0),
    0
  );
  if (totalCount === 0) return [];

  let cumulativeCount = 0;
  return histogramData.map((count, index) => {
    const validCount = count || 0;
    cumulativeCount += validCount;
    return {
      value: index * bucketSize,
      kde: validCount,
      cdf: cumulativeCount / totalCount,
    };
  });
};

const GEO_TYPE_TO_ENTITY: Record<string, PositionsEntityKind> = {
  city: PositionsEntityKind.City,
  msa: PositionsEntityKind.Msa,
  country: PositionsEntityKind.Country,
  region: PositionsEntityKind.Region,
};

export const useSalaryDistribution = (
  resolvedIds: ResolvedProfileIds,
  toggles: SalaryToggles,
  userSalary: number,
  selectedGeo: GeoLevel | null,
  companyEntity: CompanyEntity | null,
  selectedRole: RoleLevel | null
): SalaryDistributionResult => {
  const payV2Variables = useMemo(() => {
    // Entity priority: geo > company/industry
    // Role always goes into filters as custom_role (not as entity).

    const hasRole = toggles.role && selectedRole;
    const hasGeo = toggles.geo && selectedGeo;
    const hasCompany = toggles.company && companyEntity;

    // Need at least one non-role dimension for an entity, or role-only uses country fallback
    if (!hasRole && !hasGeo && !hasCompany) return undefined;

    let entityId: number;
    let entityType: PositionsEntityKind;
    let entityIsGeo = false;
    let entityIsCompany = false;

    if (hasGeo && selectedGeo) {
      entityId = selectedGeo.id;
      entityType =
        GEO_TYPE_TO_ENTITY[selectedGeo.type] ?? PositionsEntityKind.Country;
      entityIsGeo = true;
    } else if (hasCompany && companyEntity) {
      entityId = companyEntity.id;
      entityType = COMPANY_TYPE_TO_ENTITY[companyEntity.type];
      entityIsCompany = true;
    } else if (hasRole && selectedRole) {
      // Role-only: use the user's primary geo as entity (country fallback)
      const fallbackGeo =
        resolvedIds.geoLevels.find((g) => g.type === 'country') ??
        resolvedIds.geoLevels[0];
      if (fallbackGeo) {
        entityId = fallbackGeo.id;
        entityType =
          GEO_TYPE_TO_ENTITY[fallbackGeo.type] ?? PositionsEntityKind.Country;
        entityIsGeo = true;
      } else {
        return undefined;
      }
    } else {
      return undefined;
    }

    const filters: CompositionV2Filters = { raw: false };

    // Role always goes into filters as custom_role
    if (hasRole && selectedRole) {
      filters.custom_role = buildCustomRoleFilter(selectedRole);
    }
    if (toggles.seniority && resolvedIds.seniorityId) {
      filters.seniority = [resolvedIds.seniorityId];
    }
    if (hasGeo && selectedGeo && !entityIsGeo) {
      applyGeoFilter(filters, selectedGeo);
    }
    if (hasCompany && companyEntity && !entityIsCompany) {
      const filterKey = COMPANY_TYPE_TO_FILTER_KEY[companyEntity.type];
      (filters[filterKey] as number[]) = [companyEntity.id];
    }

    const req: PayV2Req = {
      entities: [{ EntID: entityId, EntType: entityType }],
      filters,
    };

    return { req };
  }, [resolvedIds, toggles, selectedGeo, companyEntity, selectedRole]);

  const [{ data, fetching, error }] = useQuery({
    query: PAY_V2_QUERY,
    variables: payV2Variables,
    pause: !payV2Variables,
  });

  const result = useMemo((): Omit<
    SalaryDistributionResult,
    'isLoading' | 'error'
  > => {
    const emptyResult = {
      distribution: [],
      label: '',
      percentile: null,
      median: null,
      p10: null,
      p90: null,
    };

    const entities = data?.pay_v2?.entities;
    if (!entities || entities.length === 0) return emptyResult;

    const firstEntity = entities[0];
    if (!firstEntity) return emptyResult;

    const histogramData = firstEntity.metrics?.histogram?.base;
    if (!histogramData || histogramData.length === 0) return emptyResult;

    const label = firstEntity.metadata?.long_name || 'Salary Distribution';
    const distribution = buildDistributionFromHistogram(
      histogramData,
      BUCKET_SIZE
    );

    if (distribution.length === 0) return emptyResult;

    const percentile = findUserPercentile(distribution, userSalary);
    const median = findPercentileValue(distribution, 0.5);
    const p10 = findPercentileValue(distribution, 0.1);
    const p90 = findPercentileValue(distribution, 0.9);

    return { distribution, label, percentile, median, p10, p90 };
  }, [data, userSalary]);

  return {
    ...result,
    isLoading: fetching,
    error: error ? 'Failed to load salary distribution' : null,
  };
};
